/**
 * Backup API Service
 * 
 * Handles API calls for system backups (database, media, logs)
 */

import apiClient from './api';

export interface BackupInfo {
    database: {
        size_bytes: number;
        size_mb: number;
    };
    media: {
        total_files: number;
        total_size_bytes: number;
        total_size_mb: number;
    };
    logs: {
        '7_days': number;
        '14_days': number;
        '30_days': number;
    };
}

export interface MediaBackupStatus {
    current_media: {
        file_count: number;
        total_size_mb: number;
        hash: string;
    };
    latest_backup: {
        has_backup: boolean;
        backup_created_at?: string;
        backup_file_count?: number;
        backup_total_size_mb?: number;
        backup_size_mb?: number;
        backup_hash?: string;
        compression_ratio?: number;
        generation_time?: number;
        is_current: boolean;
    };
}

class BackupService {
    /**
     * Get backup information (file sizes, counts)
     */
    async getBackupInfo(): Promise<BackupInfo> {
        const response = await apiClient.get<BackupInfo>('/system/backup/info/');
        return response.data;
    }

    /**
     * Download database backup
     */
    async downloadDatabaseBackup(): Promise<void> {
        const response = await apiClient.get('/system/backup/database/', {
            responseType: 'blob',
        });

        // Extract filename from Content-Disposition header
        const contentDisposition = response.headers['content-disposition'];
        let filename = 'kata_dashboard_backup.sql';
        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
            if (filenameMatch) {
                filename = filenameMatch[1];
            }
        }

        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    }

    /**
     * Get media backup status (without generating backup)
     */
    async getMediaBackupStatus(): Promise<MediaBackupStatus> {
        const response = await apiClient.get<MediaBackupStatus>('/system/backup/media/status/');
        return response.data;
    }

    /**
     * Download media files backup
     */
    async downloadMediaBackup(forceNew: boolean = false): Promise<void> {
        const params = forceNew ? '?force_new=true' : '';
        const response = await apiClient.get(`/system/backup/media/${params}`, {
            responseType: 'blob',
        });

        const contentDisposition = response.headers['content-disposition'];
        let filename = 'media_backup.tar.gz';
        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
            if (filenameMatch) {
                filename = filenameMatch[1];
            }
        }

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    }

    /**
     * Download system logs backup
     * @param days Number of days (7, 14, or 30)
     */
    async downloadLogsBackup(days: 7 | 14 | 30 = 7): Promise<void> {
        const response = await apiClient.get(`/system/backup/logs/?days=${days}`, {
            responseType: 'blob',
        });

        const contentDisposition = response.headers['content-disposition'];
        let filename = 'logs_backup.zip';
        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
            if (filenameMatch) {
                filename = filenameMatch[1];
            }
        }

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    }

    /**
     * Format bytes to human-readable size
     */
    formatBytes(bytes: number): string {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
    }
}

export default new BackupService();
